import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';

const getStorage = () => {
  // Use localStorage for localhost development
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return localStorage;
  }

  // Use CookieStorage for deployment with the exact hostname
  return new AmazonCognitoIdentity.CookieStorage({
    domain: window.location.hostname,
    secure: window.location.protocol === 'https:'
  });
};

export const getUserPool = () => {
  const poolData = {
    UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
    ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
    Storage: getStorage()
  };

  return new AmazonCognitoIdentity.CognitoUserPool(poolData);
};

export const getCognitoUser = (username) => {
  const userPool = getUserPool();

  const userData = {
    Username: username,
    Pool: userPool,
    Storage: getStorage()
  };

  return new AmazonCognitoIdentity.CognitoUser(userData);
};

export const exchangeCodeForTokens = async (code) => {
  // exchanging tokens is done via POSTing x-www-form-urlencoded data to the token endpoint
  const url = import.meta.env.VITE_COGNITO_DOMAIN + '/oauth2/token';
  const data = {
    grant_type: 'authorization_code',
    client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
    redirect_uri: window.location.origin + '?authenticate',
    code: code
  };
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: new URLSearchParams(data)
  };
  const result = await fetch(url, options);
  const json = await result.json();

  // create CognitoUserSession from tokens
  const idToken = new AmazonCognitoIdentity.CognitoIdToken({
    IdToken: json.id_token
  });
  const accessToken = new AmazonCognitoIdentity.CognitoAccessToken({
    AccessToken: json.access_token
  });
  const refreshToken = new AmazonCognitoIdentity.CognitoRefreshToken({
    RefreshToken: json.refresh_token
  });
  const sessionData = {
    IdToken: idToken,
    AccessToken: accessToken,
    RefreshToken: refreshToken
  };
  const userSession = new AmazonCognitoIdentity.CognitoUserSession(sessionData);

  const cognitoUser = getCognitoUser(idToken.payload.email);
  // refresh session - this will automatically store tokens in the configured storage
  cognitoUser.refreshSession(refreshToken, (err /*, session*/) => {
    if (err) {
      console.error('refreshSession failed', { name: err?.name, message: err?.message });
      return;
    }
  });
  return userSession;
};

/**
 * Reads the `custom:mfa_setup_required` claim from the ID token of the given session.
 *
 * Lenient parsing for resilience:
 *   - string "true"  -> true
 *   - string "false" -> false
 *   - boolean true   -> true   (defensive: claim shape might change)
 *   - boolean false  -> false
 *   - missing / undefined / null / null session -> false
 *
 * @param {import('amazon-cognito-identity-js').CognitoUserSession | null | undefined} session
 * @returns {boolean}
 */
export const getMfaSetupRequired = (session) => {
  if (!session) {
    return false;
  }

  let payload;
  try {
    payload = session.getIdToken().decodePayload();
  } catch {
    return false;
  }

  const claim = payload?.['custom:mfa_setup_required'];

  if (claim === true) {
    return true;
  }
  if (typeof claim === 'string') {
    return claim.toLowerCase() === 'true';
  }
  return false;
};

/**
 * Promise wrapper around the SDK's `refreshSession`.
 *
 * Required after `POST /mfa/verify` because the existing ID token still carries
 * `custom:mfa_setup_required="true"` until the session is refreshed.
 *
 * @param {import('amazon-cognito-identity-js').CognitoUser} cognitoUser
 * @returns {Promise<import('amazon-cognito-identity-js').CognitoUserSession>}
 */
export const forceRefreshSession = (cognitoUser) =>
  new Promise((resolve, reject) => {
    try {
      const refreshToken = cognitoUser.getSignInUserSession().getRefreshToken();
      cognitoUser.refreshSession(refreshToken, (err, session) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(session);
      });
    } catch (err) {
      reject(err);
    }
  });

/**
 * Detects whether the given Cognito session belongs to a LinkedIn-federated identity.
 *
 * Reads the `identities` claim from the ID token payload — Cognito injects this
 * array for federated users (each entry is `{ providerName, providerType, userId, ... }`).
 * Returns `true` only if the first entry's `providerType === 'LinkedIn'`.
 *
 * Used as a defensive frontend check in the post-login MFA guard: LinkedIn-federated
 * users bypass Cognito's MFA enforcement at the auth layer by design, so we short-circuit
 * the guard for them even if `custom:mfa_setup_required` is somehow set.
 *
 * Lenient parsing (mirrors `getMfaSetupRequired`):
 *   - null/undefined session            -> false
 *   - missing `identities` field        -> false
 *   - empty `identities` array          -> false
 *   - decodePayload throws              -> false
 *
 * @param {import('amazon-cognito-identity-js').CognitoUserSession | null | undefined} session
 * @returns {boolean}
 */
export const isLinkedInIdentity = (session) => {
  if (!session) {
    return false;
  }

  let payload;
  try {
    payload = session.getIdToken().decodePayload();
  } catch {
    return false;
  }

  const identities = payload?.identities;
  if (!Array.isArray(identities) || identities.length === 0) {
    return false;
  }

  return identities[0]?.providerType === 'LinkedIn';
};
